import React from 'react';
import PrimePickBadge from '../../ui/PrimePickBadge';
import { formatPrice, horseLabel, type WinnerRace } from './RecentWinners';

export interface IntelligenceStrike {
  /** Prime Pick strike rate %, 0-100. */
  strikePct: number | null;
  /** All top picks strike rate %, 0-100. */
  m1StrikePct: number | null;
}

const formatPct = (pct: number | null | undefined): string => (pct == null || isNaN(pct) ? '—' : `${pct.toFixed(1)}%`);
const barWidth = (pct: number | null | undefined): string => (pct == null || isNaN(pct) ? '0%' : `${Math.min(100, Math.max(0, pct))}%`);

const Bar: React.FC<{ label: React.ReactNode; pct?: number | null; loading: boolean; fill: string; value: string }> = ({ label, pct, loading, fill, value }) => (
  <div>
    <div className="flex justify-between text-sm mb-1.5">
      <span className="text-gray-400 inline-flex items-center gap-1.5">{label}</span>
      {loading ? <span aria-hidden="true" className="block w-11 h-4 rounded bg-white/10 animate-pulse" /> : <span className={`font-bold ${value}`}>{formatPct(pct)}</span>}
    </div>
    <div className={`h-2.5 bg-white/10 rounded-full overflow-hidden ${loading ? 'animate-pulse' : ''}`}>
      {!loading && <span className={`block h-full rounded-full transition-[width] duration-700 ease-out ${fill}`} style={{ width: barWidth(pct) }} />}
    </div>
  </div>
);

/**
 * "Live Racing Intelligence": the Prime Pick strike rate against all top picks
 * (last 90 days) and a recent real winner with its price and market edge.
 * Nothing is made up: a part with no data is left out.
 */
const LiveIntelligence: React.FC<{ featured?: WinnerRace; strike?: IntelligenceStrike; strikeLoading?: boolean }> = ({
  featured,
  strike,
  strikeLoading = false,
}) => {
  const winner = featured?.topPickWinner;
  const assessed = winner?.assessedOdds;
  const market = winner?.marketOdds;
  // the same overlay the tip cards show: how much shorter we priced it than the market
  const edge = assessed && market ? ((1 / assessed - 1 / market) / (1 / market)) * 100 : null;

  return (
    <div className="rounded-3xl p-5 bg-gradient-to-b from-deep-navy-light to-deep-navy-dark border border-white/10 shadow-[0_30px_80px_rgba(13,27,42,0.45)] text-white">
      <div className="flex justify-between font-bold mb-4">
        <span className="text-racing-green-light">Live Racing Intelligence</span>
        {winner && <span className="text-race-gold">● Recent Result</span>}
      </div>

      {(strikeLoading || strike) && (
        <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-4 mb-4" aria-busy={strikeLoading}>
          <div className="flex justify-between items-start mb-5 gap-3">
            <div>
              <strong className="block">Prime Pick Strike Rate</strong>
              <small className="text-gray-400">Prime Picks are our stronger tips · last 90 days</small>
            </div>
            {strikeLoading ? (
              <span aria-hidden="true" className="block w-[74px] h-[26px] rounded-md bg-white/10 animate-pulse" />
            ) : (
              <span className="text-race-gold text-2xl font-black">{formatPct(strike?.strikePct)}</span>
            )}
          </div>
          <div className="grid gap-4">
            <Bar label="All Top Picks" pct={strike?.m1StrikePct} loading={strikeLoading} fill="bg-racing-green" value="text-white" />
            <Bar label={<><PrimePickBadge iconOnly /> Prime Pick</>} pct={strike?.strikePct} loading={strikeLoading} fill="bg-gradient-to-r from-racing-green to-race-gold" value="text-race-gold" />
          </div>
        </div>
      )}

      {winner && (
        <>
          <div className={`grid gap-3.5 ${edge != null ? 'grid-cols-2' : 'grid-cols-1'}`}>
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-4">
              <div className="flex items-center justify-between gap-2">
                <strong className="text-gray-400">Top Pick</strong>
                {featured?.isConsensus && <PrimePickBadge iconOnly />}
              </div>
              <h3 className="text-xl font-bold mt-3 mb-1.5">{horseLabel(winner)}</h3>
              <div className="h-2.5 bg-white/10 rounded-full overflow-hidden my-3.5">
                <span className="block h-full w-full bg-gradient-to-r from-racing-green to-racing-green-light rounded-full" />
              </div>
              <small className="text-racing-green-light font-black">Won {formatPrice(winner.winningPrice)}</small>
            </div>

            {edge != null && (
              <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-4">
                <strong className="text-gray-400">Market Edge</strong>
                <h3 className="text-xl font-bold mt-3 mb-1.5">{`${edge >= 0 ? '+' : ''}${edge.toFixed(1)}%`}</h3>
                <p className="text-gray-400 text-sm">Value vs market price</p>
                <div className="grid grid-cols-2 gap-2 mt-3.5">
                  <div>
                    <div className="text-gray-400 text-xs">Our price</div>
                    <div className="font-black">{formatPrice(assessed)}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-xs">Market</div>
                    <div className="font-black">{formatPrice(market)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-3.5 grid grid-cols-3 gap-2.5 p-4 rounded-2xl bg-racing-green/10 border border-racing-green/25 text-sm">
            <div className="grid gap-1">
              <strong>Race</strong>
              <span className="text-gray-400">
                {featured?.trackName ?? '—'}
                {featured?.raceNumber ? ` R${featured.raceNumber}` : ''}
              </span>
            </div>
            <div className="grid gap-1">
              <strong>Price</strong>
              <span className="text-gray-400">{formatPrice(winner.winningPrice)}</span>
            </div>
            <div className="grid gap-1">
              <strong>Selection</strong>
              <span className="text-race-gold font-black">{featured?.isConsensus ? 'Prime Pick' : 'Top Pick'}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LiveIntelligence;
